import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import {
  createAgentInAppPopup,
  fetchAgentPlayers,
  fetchDashboard,
  fetchManagedAgents,
  fetchShops,
} from '../services/api';
import type { AgentDashboardData, AgentPlayer, AgentShop, SuperManagedAgent } from '../types';

const TITLE_MAX = 500;
/** Portal UI only; backend does not enforce */
const BODY_MAX = 1000;
type TargetKind = 'agent' | 'shop' | 'player';

export default function SendShopPopupPage() {
  const [dash, setDash] = useState<AgentDashboardData | null>(null);
  const [agents, setAgents] = useState<SuperManagedAgent[]>([]);
  const [shops, setShops] = useState<AgentShop[]>([]);
  const [players, setPlayers] = useState<AgentPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetKind, setTargetKind] = useState<TargetKind>('agent');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [displayMode, setDisplayMode] = useState<'once' | 'sticky_daily'>('once');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a, s, p] = await Promise.all([
        fetchDashboard(),
        fetchManagedAgents(),
        fetchShops(),
        fetchAgentPlayers(),
      ]);
      if (d.success && d.data) setDash(d.data);
      if (a.success && a.data) {
        setAgents(a.data.filter((x) => x.status === 'active'));
      }
      if (s.success && s.data) {
        setShops(s.data.filter((x) => x.status === 'active'));
      }
      if (p.success && p.data) {
        setPlayers(p.data.filter((x) => x.status === 'active'));
      }
    } catch {
      toast.error('Could not load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dash?.can_delegate_popups) return;
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (selected.size === 0) {
      const kindLabel = targetKind === 'agent' ? 'agent' : targetKind === 'shop' ? 'shop' : 'player';
      toast.error(`Select at least one ${kindLabel}`);
      return;
    }
    if (displayMode === 'sticky_daily' && !expiresAt.trim()) {
      toast.error('Campaign end date is required for sticky popups');
      return;
    }
    setSubmitting(true);
    try {
      const selectedIds = Array.from(selected);
      const res = await createAgentInAppPopup({
        title: title.trim(),
        body: body.trim(),
        targetAgentIds: targetKind === 'agent' ? selectedIds : [],
        targetShopIds: targetKind === 'shop' ? selectedIds : [],
        targetUserIds: targetKind === 'player' ? selectedIds : [],
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        displayMode,
      });
      if (res.success) {
        toast.success('Popup sent');
        setTitle('');
        setBody('');
        setExpiresAt('');
        setDisplayMode('once');
        setSelected(new Set());
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageLoadingIndicator variant="page" />
      </AppLayout>
    );
  }

  if (dash && !dash.can_delegate_popups) {
    return <Navigate to="/dashboard" replace />;
  }

  const items: (SuperManagedAgent | AgentShop | AgentPlayer)[] =
    targetKind === 'agent' ? agents : targetKind === 'shop' ? shops : players;

  const audiencePlural =
    targetKind === 'agent' ? 'Agents' : targetKind === 'shop' ? 'Shops' : 'Players';

  return (
    <AppLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-2">Send popup</h1>
      <p className="text-sm text-gray-600 mb-6 max-w-xl">
        Message appears as an in-app notification for selected managed agents, shops, or players under your hierarchy.
        Message body is limited to {BODY_MAX} characters in this portal (same rule as admin).
      </p>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 space-y-2">
          <p className="text-sm font-medium text-gray-800">Audience</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-inapp-target"
              checked={targetKind === 'agent'}
              onChange={() => {
                setTargetKind('agent');
                setSelected(new Set());
              }}
            />
            <span>Agents</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-inapp-target"
              checked={targetKind === 'shop'}
              onChange={() => {
                setTargetKind('shop');
                setSelected(new Set());
              }}
            />
            <span>Shops</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-inapp-target"
              checked={targetKind === 'player'}
              onChange={() => {
                setTargetKind('player');
                setSelected(new Set());
              }}
            />
            <span>Players</span>
          </label>
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <span className="text-xs text-gray-500 tabular-nums">
              {title.length}/{TITLE_MAX}
            </span>
          </div>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            maxLength={TITLE_MAX}
            required
          />
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-gray-700">Message</label>
            <span className="text-xs text-gray-500 tabular-nums">
              {body.length}/{BODY_MAX}
            </span>
          </div>
          <textarea
            className="w-full min-h-[140px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            maxLength={BODY_MAX}
            required
          />
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 space-y-2">
          <p className="text-sm font-medium text-gray-800">Display</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-inapp-display"
              checked={displayMode === 'once'}
              onChange={() => setDisplayMode('once')}
            />
            <span>
              Once per {targetKind === 'agent' ? 'agent' : targetKind === 'shop' ? 'shop' : 'player'} (hidden after OK)
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-inapp-display"
              checked={displayMode === 'sticky_daily'}
              onChange={() => setDisplayMode('sticky_daily')}
            />
            <span>
              Sticky until end date — shows again on each reload;{' '}
              {targetKind === 'agent' ? 'agents' : targetKind === 'shop' ? 'shops' : 'players'} can OK (until next
              load) or opt out
            </span>
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {displayMode === 'sticky_daily' ? 'Campaign end (required)' : 'Hide after (optional)'}
          </label>
          <input
            type="datetime-local"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            required={displayMode === 'sticky_daily'}
          />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{audiencePlural}</p>
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">
              No active {targetKind === 'agent' ? 'agents' : targetKind === 'shop' ? 'shops' : 'players'}.
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
              {items.map((it) => {
                if (targetKind === 'agent') {
                  const ag = it as SuperManagedAgent;
                  return (
                    <label
                      key={ag.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                    >
                      <input type="checkbox" checked={selected.has(ag.id)} onChange={() => toggle(ag.id)} />
                      <span className="font-medium text-gray-900">{ag.name}</span>
                      <span className="text-gray-500 truncate">{ag.email}</span>
                    </label>
                  );
                }
                if (targetKind === 'shop') {
                  const shop = it as AgentShop;
                  return (
                    <label
                      key={shop.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(shop.id)}
                        onChange={() => toggle(shop.id)}
                      />
                      <span className="font-medium text-gray-900">{shop.name}</span>
                      <span className="text-gray-500 truncate">{shop.email}</span>
                    </label>
                  );
                }
                const player = it as AgentPlayer;
                return (
                  <label
                    key={player.id}
                    className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(player.id)}
                      onChange={() => toggle(player.id)}
                    />
                    <span className="font-medium text-gray-900 truncate">
                      {player.phone || player.email || player.username || player.id}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={submitting || items.length === 0}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? 'Sending…' : 'Send popup'}
        </button>
      </form>
    </AppLayout>
  );
}
